import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  User,
  Briefcase,
  MapPin,
  FileText,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Upload,
  X,
  Check,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const steps = [
  { id: 1, title: 'Datos Personales', icon: User },
  { id: 2, title: 'Información Profesional', icon: Briefcase },
  { id: 3, title: 'Dirección', icon: MapPin },
  { id: 4, title: 'Documentación', icon: FileText },
  { id: 5, title: 'Confirmación', icon: CheckCircle2 }
];

const categoriasSocio = [
  { value: 'residente_1', label: 'Residente/Concurrente 1' },
  { value: 'residente_2', label: 'Residente/Concurrente 2' },
  { value: 'residente_3', label: 'Residente/Concurrente 3' },
  { value: 'residente_4', label: 'Residente/Concurrente 4' },
  { value: 'residente_5', label: 'Residente/Concurrente 5' },
  { value: 'especialista', label: 'Especialista' },
  { value: 'vitalicio', label: 'Vitalicio' },
  { value: 'honorario', label: 'Honorario' },
  { value: 'suscriptor', label: 'Suscriptor' }
];

const subespecialidades = [
  'Oftalmología General',
  'Baja Visión',
  'Catarata',
  'Córnea',
  'Defensa Salud Visual',
  'Estrabismo',
  'Glaucoma',
  'Gremialismo',
  'Infectología',
  'Neurooftalmología',
  'Oculoplástica y vías lagrimales',
  'Órbita',
  'Oftalmopediatría',
  'Refracción',
  'Refractiva',
  'Praxis Médica',
  'Presbicia',
  'Retina',
  'Superficie Ocular',
  'Uveítis',
  'Otra'
];

const provincias = [
  'Buenos Aires', 'CABA', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes',
  'Entre Ríos', 'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones',
  'Neuquén', 'Río Negro', 'Salta', 'San Juan', 'San Luis', 'Santa Cruz', 'Santa Fe',
  'Santiago del Estero', 'Tierra del Fuego', 'Tucumán'
];

const opcionesPago = [
  { value: 'debito_automatico', label: 'Débito automático' },
  { value: 'pago_anual', label: 'Pago anual' },
  { value: 'pago_semestral', label: 'Pago semestral' },
  { value: 'transferencia', label: 'Transferencia bancaria' }
];

const initialFormData = {
  // Datos personales
  fecha_nacimiento: '',
  fecha_alta: new Date().toISOString().split('T')[0],
  estatus_profesional: '',
  categoria_socio: '',
  fecha_titulo_especialista: '',
  inicio_residencia: '',
  fin_residencia: '',
  
  // Matrícula
  matricula_nacional: '',
  matricula_provincial: '',
  provincia_matricula: '',
  
  // Pago
  opcion_pago: '',
  cuit_cuil: '',
  
  // Subespecialidades
  subespecialidad_principal: '',
  subespecialidad_2: '',
  subespecialidad_3: '',
  
  // Dirección
  direccion: '',
  codigo_postal: '',
  localidad: '',
  provincia: '',
  pais: 'Argentina',
  
  // Info terceros
  autorizacion_datos: '',
  email_terceros: '',
  telefono_terceros: '',
  
  // Documentos
  documento_identidad: null,
  documento_matricula: null,
  titulo_medico: null,
  titulo_especialista: null,
  certificado_residencia: null,
  
  // Términos
  acepta_terminos: false
};

const AltaSocios = () => {
  const { getAuthHeaders } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [existingSocio, setExistingSocio] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [fileNames, setFileNames] = useState({});

  useEffect(() => {
    const checkExistingSocio = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/socios/me`, {
          headers: getAuthHeaders()
        });
        if (response.data) {
          setExistingSocio(response.data);
        }
      } catch (error) {
        // No existing socio
      } finally {
        setCheckingExisting(false);
      }
    };
    checkExistingSocio();
  }, [getAuthHeaders]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (field, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('El archivo no puede superar los 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, [field]: reader.result }));
        setFileNames(prev => ({ ...prev, [field]: file.name }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = (field) => {
    setFormData(prev => ({ ...prev, [field]: null }));
    setFileNames(prev => ({ ...prev, [field]: null }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.fecha_nacimiento) {
          toast.error('Ingrese su fecha de nacimiento');
          return false;
        }
        if (!formData.categoria_socio) {
          toast.error('Seleccione su categoría de socio');
          return false;
        }
        return true;
      case 2:
        if (!formData.cuit_cuil || formData.cuit_cuil.length !== 11) {
          toast.error('Ingrese un CUIT/CUIL válido (11 dígitos)');
          return false;
        }
        if (!formData.opcion_pago) {
          toast.error('Seleccione una opción de pago');
          return false;
        }
        if (!formData.subespecialidad_principal) {
          toast.error('Seleccione su subespecialidad principal');
          return false;
        }
        return true;
      case 3:
        if (!formData.direccion || !formData.codigo_postal || !formData.localidad || !formData.provincia) {
          toast.error('Complete todos los campos de dirección');
          return false;
        }
        if (!formData.autorizacion_datos) {
          toast.error('Seleccione una opción de autorización de datos');
          return false;
        }
        return true;
      case 4:
        if (!formData.documento_identidad) {
          toast.error('Debe adjuntar su documento de identidad');
          return false;
        }
        return true;
      case 5:
        if (!formData.acepta_terminos) {
          toast.error('Debe aceptar los términos y condiciones');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/socios/alta`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('Solicitud de alta enviada correctamente');
      setExistingSocio({ estado: 'pendiente', datos: formData });
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  if (checkingExisting) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  if (existingSocio) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="alta-socios-existing">
        <Card className="cao-card">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
              {existingSocio.estado === 'pendiente' ? (
                <AlertCircle className="w-8 h-8 text-yellow-600" />
              ) : existingSocio.estado === 'aprobado' ? (
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              ) : (
                <AlertCircle className="w-8 h-8 text-red-600" />
              )}
            </div>
            <CardTitle className="text-2xl text-[#1E3876]">
              Solicitud de Alta de Socio
            </CardTitle>
            <CardDescription>
              Ya tiene una solicitud registrada
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Estado:</span>
                <Badge className={
                  existingSocio.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-700' :
                  existingSocio.estado === 'aprobado' ? 'bg-green-100 text-green-700' :
                  'bg-red-100 text-red-700'
                }>
                  {existingSocio.estado.charAt(0).toUpperCase() + existingSocio.estado.slice(1)}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Categoría:</span>
                <span className="font-medium">
                  {categoriasSocio.find(c => c.value === existingSocio.datos?.categoria_socio)?.label || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Subespecialidad:</span>
                <span className="font-medium text-right max-w-[200px]">
                  {existingSocio.datos?.subespecialidad_principal || 'N/A'}
                </span>
              </div>
            </div>
            
            {existingSocio.estado === 'pendiente' && (
              <p className="text-center text-gray-600">
                Su solicitud está siendo revisada por el equipo del CAO. 
                Le notificaremos por email cuando sea procesada.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="alta-socios-form">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#1E3876] mb-2">Alta de Socios</h1>
        <p className="text-gray-600">
          Complete el siguiente formulario para asociarse al Consejo Argentino de Oftalmología
        </p>
      </div>

      {/* Stepper */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex items-center min-w-max px-2">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`cao-stepper-circle ${
                    currentStep > step.id ? 'completed' :
                    currentStep === step.id ? 'active' : 'pending'
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`text-xs mt-2 whitespace-nowrap ${
                  currentStep === step.id ? 'text-[#2853B9] font-medium' : 'text-gray-500'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`cao-stepper-line w-16 mx-2 ${
                  currentStep > step.id ? 'completed' : 'pending'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Card */}
      <Card className="cao-card">
        <CardContent className="p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Step 1: Datos Personales */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h2 className="form-section-title">Datos Personales</h2>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="cao-label">Fecha de Nacimiento *</Label>
                      <Input
                        type="date"
                        className="cao-input"
                        value={formData.fecha_nacimiento}
                        onChange={(e) => handleInputChange('fecha_nacimiento', e.target.value)}
                        data-testid="fecha-nacimiento-input"
                      />
                    </div>
                    
                    <div>
                      <Label className="cao-label">Fecha de Alta</Label>
                      <Input
                        type="date"
                        className="cao-input"
                        value={formData.fecha_alta}
                        onChange={(e) => handleInputChange('fecha_alta', e.target.value)}
                        disabled
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="cao-label">Categoría de Socio CAO *</Label>
                    <Select
                      value={formData.categoria_socio}
                      onValueChange={(value) => handleInputChange('categoria_socio', value)}
                    >
                      <SelectTrigger className="cao-input" data-testid="categoria-socio-select">
                        <SelectValue placeholder="Seleccione una categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        {categoriasSocio.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-gray-500 mt-1">
                      Si esta información está desactualizada, podrá solicitar una recategorización posteriormente.
                    </p>
                  </div>

                  {formData.categoria_socio?.includes('residente') && (
                    <div className="grid md:grid-cols-2 gap-6 p-4 bg-blue-50 rounded-lg">
                      <div>
                        <Label className="cao-label">Inicio de Residencia/Concurrencia</Label>
                        <Input
                          type="date"
                          className="cao-input"
                          value={formData.inicio_residencia}
                          onChange={(e) => handleInputChange('inicio_residencia', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="cao-label">Finalización de Residencia/Concurrencia</Label>
                        <Input
                          type="date"
                          className="cao-input"
                          value={formData.fin_residencia}
                          onChange={(e) => handleInputChange('fin_residencia', e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {formData.categoria_socio === 'especialista' && (
                    <div>
                      <Label className="cao-label">Fecha de Título de Especialista</Label>
                      <Input
                        type="date"
                        className="cao-input"
                        value={formData.fecha_titulo_especialista}
                        onChange={(e) => handleInputChange('fecha_titulo_especialista', e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Información Profesional */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h2 className="form-section-title">Información Profesional</h2>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="cao-label">Matrícula Nacional</Label>
                      <Input
                        type="text"
                        className="cao-input"
                        placeholder="Número de matrícula"
                        value={formData.matricula_nacional}
                        onChange={(e) => handleInputChange('matricula_nacional', e.target.value)}
                        data-testid="matricula-nacional-input"
                      />
                    </div>
                    <div>
                      <Label className="cao-label">Matrícula Provincial</Label>
                      <Input
                        type="text"
                        className="cao-input"
                        placeholder="Número de matrícula"
                        value={formData.matricula_provincial}
                        onChange={(e) => handleInputChange('matricula_provincial', e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="cao-label">Provincia de Matrícula</Label>
                    <Select
                      value={formData.provincia_matricula}
                      onValueChange={(value) => handleInputChange('provincia_matricula', value)}
                    >
                      <SelectTrigger className="cao-input">
                        <SelectValue placeholder="Seleccione una provincia" />
                      </SelectTrigger>
                      <SelectContent>
                        {provincias.map((prov) => (
                          <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="cao-label">CUIT/CUIL *</Label>
                    <Input
                      type="text"
                      className="cao-input"
                      placeholder="20123456780 (11 dígitos sin guiones)"
                      maxLength={11}
                      value={formData.cuit_cuil}
                      onChange={(e) => handleInputChange('cuit_cuil', e.target.value.replace(/\D/g, ''))}
                      data-testid="cuit-input"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Su número de CUIT/CUIL es indispensable para su facturación.
                    </p>
                  </div>

                  <div>
                    <Label className="cao-label">Opción de Pago *</Label>
                    <Select
                      value={formData.opcion_pago}
                      onValueChange={(value) => handleInputChange('opcion_pago', value)}
                    >
                      <SelectTrigger className="cao-input" data-testid="opcion-pago-select">
                        <SelectValue placeholder="Seleccione una opción" />
                      </SelectTrigger>
                      <SelectContent>
                        {opcionesPago.map((op) => (
                          <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.opcion_pago === 'debito_automatico' && (
                      <p className="text-sm text-blue-600 mt-1">
                        Al enviar el formulario, se le solicitará que complete los datos de su tarjeta.
                      </p>
                    )}
                  </div>

                  <div className="space-y-4">
                    <Label className="cao-label">Subespecialidades</Label>
                    
                    <div>
                      <Label className="text-sm text-gray-600">Subespecialidad Principal *</Label>
                      <Select
                        value={formData.subespecialidad_principal}
                        onValueChange={(value) => handleInputChange('subespecialidad_principal', value)}
                      >
                        <SelectTrigger className="cao-input" data-testid="subespecialidad-principal-select">
                          <SelectValue placeholder="Seleccione subespecialidad" />
                        </SelectTrigger>
                        <SelectContent>
                          {subespecialidades.map((sub) => (
                            <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-gray-600">Subespecialidad 2</Label>
                        <Select
                          value={formData.subespecialidad_2}
                          onValueChange={(value) => handleInputChange('subespecialidad_2', value)}
                        >
                          <SelectTrigger className="cao-input">
                            <SelectValue placeholder="Opcional" />
                          </SelectTrigger>
                          <SelectContent>
                            {subespecialidades.map((sub) => (
                              <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Subespecialidad 3</Label>
                        <Select
                          value={formData.subespecialidad_3}
                          onValueChange={(value) => handleInputChange('subespecialidad_3', value)}
                        >
                          <SelectTrigger className="cao-input">
                            <SelectValue placeholder="Opcional" />
                          </SelectTrigger>
                          <SelectContent>
                            {subespecialidades.map((sub) => (
                              <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Dirección */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <h2 className="form-section-title">Dirección de Correspondencia</h2>
                  
                  <div>
                    <Label className="cao-label">Dirección *</Label>
                    <Input
                      type="text"
                      className="cao-input"
                      placeholder="Calle y número"
                      value={formData.direccion}
                      onChange={(e) => handleInputChange('direccion', e.target.value)}
                      data-testid="direccion-input"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label className="cao-label">Código Postal *</Label>
                      <Input
                        type="text"
                        className="cao-input"
                        placeholder="1234"
                        value={formData.codigo_postal}
                        onChange={(e) => handleInputChange('codigo_postal', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="cao-label">Localidad *</Label>
                      <Input
                        type="text"
                        className="cao-input"
                        placeholder="Ciudad"
                        value={formData.localidad}
                        onChange={(e) => handleInputChange('localidad', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="cao-label">Provincia *</Label>
                      <Select
                        value={formData.provincia}
                        onValueChange={(value) => handleInputChange('provincia', value)}
                      >
                        <SelectTrigger className="cao-input">
                          <SelectValue placeholder="Seleccione" />
                        </SelectTrigger>
                        <SelectContent>
                          {provincias.map((prov) => (
                            <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="border-t pt-6 mt-6">
                    <h3 className="font-semibold text-[#1E3876] mb-4">Información para Terceros</h3>
                    
                    <div>
                      <Label className="cao-label">Autorización de Datos *</Label>
                      <RadioGroup
                        value={formData.autorizacion_datos}
                        onValueChange={(value) => handleInputChange('autorizacion_datos', value)}
                        className="space-y-3 mt-2"
                      >
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="publico" id="publico" />
                          <Label htmlFor="publico" className="font-normal cursor-pointer">
                            Autorizo al CAO a publicar mis datos de contacto al público
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="colegas" id="colegas" />
                          <Label htmlFor="colegas" className="font-normal cursor-pointer">
                            Autorizo al CAO a publicar mis datos de contacto sólo a colegas
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="ninguno" id="ninguno" />
                          <Label htmlFor="ninguno" className="font-normal cursor-pointer">
                            No autorizo a publicar ningún dato
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {formData.autorizacion_datos !== 'ninguno' && (
                      <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <Label className="cao-label">Email para terceros</Label>
                          <Input
                            type="email"
                            className="cao-input"
                            placeholder="email@ejemplo.com"
                            value={formData.email_terceros}
                            onChange={(e) => handleInputChange('email_terceros', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="cao-label">Teléfono para terceros</Label>
                          <Input
                            type="tel"
                            className="cao-input"
                            placeholder="+54 11 1234-5678"
                            value={formData.telefono_terceros}
                            onChange={(e) => handleInputChange('telefono_terceros', e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 4: Documentación */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <h2 className="form-section-title">Documentación</h2>
                  <p className="text-gray-600 -mt-2">
                    Suba los documentos requeridos en formato PDF o imagen (máx. 5MB cada uno)
                  </p>
                  
                  {[
                    { field: 'documento_identidad', label: 'Documento de Identidad *', required: true },
                    { field: 'documento_matricula', label: 'Matrícula Profesional' },
                    { field: 'titulo_medico', label: 'Título de Médico' },
                    { field: 'titulo_especialista', label: 'Título de Especialista' },
                    { field: 'certificado_residencia', label: 'Certificado de Residencia/Concurrencia' }
                  ].map((doc) => (
                    <div key={doc.field}>
                      <Label className="cao-label">{doc.label}</Label>
                      {formData[doc.field] ? (
                        <div className="file-upload-zone has-file flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                            <span className="text-gray-700">{fileNames[doc.field] || 'Archivo cargado'}</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(doc.field)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="file-upload-zone block">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            onChange={(e) => handleFileChange(doc.field, e)}
                            data-testid={`${doc.field}-input`}
                          />
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600">
                            Haga clic para seleccionar o arrastre el archivo aquí
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            PDF, JPG o PNG (máx. 5MB)
                          </p>
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Step 5: Confirmación */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <h2 className="form-section-title">Confirmación y Términos</h2>
                  
                  <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                    <h3 className="font-semibold text-[#1E3876]">Resumen de su solicitud</h3>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Categoría:</span>
                        <p className="font-medium">
                          {categoriasSocio.find(c => c.value === formData.categoria_socio)?.label}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">CUIT/CUIL:</span>
                        <p className="font-medium">{formData.cuit_cuil}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Subespecialidad Principal:</span>
                        <p className="font-medium">{formData.subespecialidad_principal}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Forma de Pago:</span>
                        <p className="font-medium">
                          {opcionesPago.find(o => o.value === formData.opcion_pago)?.label}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <span className="text-gray-500">Dirección:</span>
                        <p className="font-medium">
                          {formData.direccion}, {formData.localidad}, {formData.provincia}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-6 space-y-4 text-sm">
                    <h3 className="font-semibold text-[#1E3876]">Notificaciones Legales</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>
                        <strong>Domicilio electrónico:</strong> El socio presta conformidad con el sistema 
                        de notificaciones e informaciones por email del CAO.
                      </li>
                      <li>
                        <strong>Aumentos:</strong> El socio autoriza al CAO a trasladar los aumentos que 
                        disponga el Comité Ejecutivo en el costo de la cuota social.
                      </li>
                      <li>
                        <strong>Baja de socios:</strong> El trámite de baja requiere tener la cuota social 
                        al día. No se realizan reintegros.
                      </li>
                      <li>
                        <strong>Socios morosos:</strong> Todo socio que adeude dos cuotas anuales será 
                        declarado como "cesante por morosidad".
                      </li>
                    </ul>
                  </div>

                  <div className="flex items-start gap-3 p-4 border rounded-lg">
                    <Checkbox
                      id="terminos"
                      checked={formData.acepta_terminos}
                      onCheckedChange={(checked) => handleInputChange('acepta_terminos', checked)}
                      data-testid="acepta-terminos-checkbox"
                    />
                    <Label htmlFor="terminos" className="font-normal cursor-pointer leading-relaxed">
                      Acepto los términos y condiciones del Consejo Argentino de Oftalmología y 
                      confirmo que la información proporcionada es correcta.
                    </Label>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="rounded-full"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Anterior
            </Button>

            {currentStep < 5 ? (
              <Button
                type="button"
                onClick={nextStep}
                className="bg-[#2853B9] hover:bg-[#1E3876] rounded-full"
                data-testid="siguiente-btn"
              >
                Siguiente
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="bg-orange-500 hover:bg-orange-600 rounded-full"
                data-testid="enviar-solicitud-btn"
              >
                {loading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Enviando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Enviar Solicitud
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AltaSocios;
